/**
 * Failure kinds the UI can act on. The API owns stable machine codes; mapping
 * them to localized copy is a frontend responsibility, so this enum is the only
 * vocabulary components use to choose a message id.
 */
export type CoreErrorKind =
  /** No valid session cookie: the caller must return to login. */
  | 'unauthenticated'
  /** Authenticated, but this account may not perform the request. */
  | 'forbidden'
  /** No such resource, or one outside the session's clinic. The API does not
   *  distinguish the two, and neither may the UI. */
  | 'not-found'
  /** Login rejected. Deliberately does not distinguish unknown user from bad password. */
  | 'invalid-credentials'
  /** Login attempt throttle engaged. */
  | 'throttled'
  /** Transport failure, or the API is up but not serving. */
  | 'unavailable'
  /** Input the server rejected as invalid; the form can be corrected. */
  | 'invalid-input'
  /** The declared or detected media type is not accepted. */
  | 'unsupported-media'
  /** The API refused the size and stated the limit. */
  | 'too-large'
  /**
   * Something in front of the API refused the body without stating a limit.
   * Naming a number here would send someone shrinking a file to fit a
   * constraint that was never the real one.
   */
  | 'too-large-unstated'
  /** An idempotency key was reused with different content or metadata. */
  | 'conflict'
  /** A 2xx response whose body did not carry the fields the UI renders. */
  | 'malformed'

/**
 * The runtime owner of every Core failure kind.
 *
 * Keep this list exhaustive: refusal resolvers expand each surface's explicit
 * overrides across it, so a newly introduced kind cannot silently disappear
 * into whichever private switch a component happened to maintain.
 */
export const CORE_ERROR_KINDS = [
  'unauthenticated',
  'forbidden',
  'not-found',
  'invalid-credentials',
  'throttled',
  'unavailable',
  'invalid-input',
  'unsupported-media',
  'too-large',
  'too-large-unstated',
  'conflict',
  'malformed',
] as const satisfies readonly CoreErrorKind[]

const CORE_ERROR_KINDS_ARE_EXHAUSTIVE:
  Exclude<CoreErrorKind, (typeof CORE_ERROR_KINDS)[number]> extends never ? true : never = true
void CORE_ERROR_KINDS_ARE_EXHAUSTIVE

export class CoreError extends Error {
  readonly kind: CoreErrorKind
  readonly status: number | undefined
  /**
   * The API's stable machine code, carried verbatim.
   *
   * `kind` is what most callers act on. A few need finer detail than a kind can
   * express — two conflicts that need opposite responses from the clinician
   * share one kind and one status, and only the code tells them apart.
   */
  readonly code: string | undefined

  /** A structured JSON error envelope; endpoint readers validate their fields. */
  readonly details: Readonly<Record<string, unknown>> | undefined

  constructor(
    kind: CoreErrorKind,
    message: string,
    status?: number,
    code?: string,
    details?: Readonly<Record<string, unknown>>,
  ) {
    super(message)
    this.name = 'CoreError'
    this.kind = kind
    this.status = status
    this.code = code
    this.details = details
  }
}

/** The API's machine code for a failure, when it sent one. */
export function coreErrorCode(error: unknown): string | undefined {
  return isCoreError(error) ? error.code : undefined
}

/**
 * A READ THAT SUCCEEDED AND CARRIED NOTHING TO RENDER.
 *
 * Three surfaces met the identical shape — `!data` with no error — and each
 * spelled `'malformed'` for itself, beside its own comment explaining why. It
 * is one situation, so it is one value. `malformed` is the right kind rather
 * than an outage: the read SUCCEEDED, so the shared policy withholds the retry
 * because asking again returns the same body.
 */
export const MALFORMED_ANSWER = new CoreError(
  'malformed',
  'the read succeeded and carried nothing to render',
)

/**
 * A BODY THAT DID NOT MATCH THE CONTRACT, NAMED BY WHAT WAS BEING READ.
 *
 * Four clients each wrote a factory for this one refusal and each chose its own
 * sentence — "is incomplete", "did not match the contract", "schedule … is
 * incomplete", "is malformed" (#464) — and the fourth THREW where the others
 * returned, so a call site read differently depending on which client it was
 * in. One factory, one sentence, and every caller throws it.
 *
 * The subject is a NOUN PHRASE for what was being read — a whole payload
 * (`estimates`, `day board lanes`) or the one field that failed (`capacity
 * value`) — because the schedule adapters refuse per field while the finance
 * clients refuse per payload, and one sentence has to carry both. A clinician
 * never sees this string; the developer who does is looking for which read
 * refused.
 */
export function malformedWire(subject: string): CoreError {
  return new CoreError('malformed', `${subject} did not match the contract`)
}

export function isCoreError(value: unknown): value is CoreError {
  return value instanceof CoreError
}

export function coreErrorKind(value: unknown): CoreErrorKind {
  return isCoreError(value) ? value.kind : 'unavailable'
}

/*
 * THE FOUR QUESTIONS THE APP ASKS OF A REFUSAL THAT ARE NOT ABOUT WORDS.
 *
 * What a refusal SAYS is a resolution table's decision and nothing else's.
 * What the app DOES about one is a different question, and it was asked by
 * comparing `coreErrorKind(error)` to a string literal in thirty files: a
 * redirect to login, a refetch after a lost race, a row dropped because the
 * server no longer has it, a section withheld from an account that may not see
 * it. Each is a decision about behaviour that happens to be spelled as a kind.
 *
 * Named here, and named as the QUESTION rather than as the kind, because a
 * generic `hasKind(error, 'conflict')` is `coreErrorKind` with a longer name:
 * it would satisfy any rule about where the kind is read while leaving the
 * decision exactly as scattered. There are four of these, they are the whole
 * set, and a fifth is a real design question rather than a call site.
 */

/** No usable session: the caller must return to login. */
export function isSessionGone(error: unknown): boolean {
  return coreErrorKind(error) === 'unauthenticated'
}

/** The object moved under the writer. A re-read can reconcile it. */
export function isWriteConflict(error: unknown): boolean {
  return coreErrorKind(error) === 'conflict'
}

/** The server has no such object — gone, or never in this clinic's reach. */
export function isMissing(error: unknown): boolean {
  return coreErrorKind(error) === 'not-found'
}

/** Authenticated, and this account may not. Never a reason to retry. */
export function isRefused(error: unknown): boolean {
  return coreErrorKind(error) === 'forbidden'
}

export interface CoreErrorResolution<T> {
  readonly byCode: Readonly<Record<string, T>>
  readonly byKind: Readonly<Record<CoreErrorKind, T>>
  readonly fallback: T
}

/**
 * Define a surface's refusal policy without restating the full Core kind list.
 * Unmentioned kinds deliberately receive the named fallback, while the shared
 * kind owner above keeps that fallback expansion exhaustive.
 */
export function defineCoreErrorResolution<T>(
  fallback: T,
  options: {
    readonly byCode?: Readonly<Record<string, T>>
    readonly byKind?: Readonly<Partial<Record<CoreErrorKind, T>>>
  } = {},
): CoreErrorResolution<T> {
  const byKind = Object.fromEntries(
    CORE_ERROR_KINDS.map((kind) => [kind, options.byKind?.[kind] ?? fallback]),
  ) as Record<CoreErrorKind, T>

  return {
    byCode: options.byCode ?? {},
    byKind,
    fallback,
  }
}

/** Resolve a Core refusal by stable machine code first, then by failure kind. */
export function resolveCoreError<T>(error: unknown, resolution: CoreErrorResolution<T>): T {
  const code = coreErrorCode(error)
  if (code !== undefined && Object.hasOwn(resolution.byCode, code)) {
    return resolution.byCode[code]!
  }
  return isCoreError(error) ? resolution.byKind[error.kind] : resolution.fallback
}

/**
 * One submission of one act, as a mutation reports it. Structural on purpose:
 * this layer owes nothing to the query library, and every hook in the product
 * already answers these three questions.
 */
export interface SubmittedAct {
  readonly isError: boolean
  readonly error: unknown
  /** 0 until the act has been submitted once, then the time it last was. */
  readonly submittedAt: number
}

/**
 * WHICH OF SEVERAL ACTS IS REFUSING NOW — the one the person last asked for,
 * never the first in some list.
 *
 * A screen that draws ONE refusal line for several independent acts cannot ask
 * "is any of them failing": a mutation keeps its refusal until it is submitted
 * again, so a fixed order reports whichever act failed FIRST and leaves it
 * there while the next act refuses for a different reason. With a sentence per
 * act — which is the point of resolving refusals at all — that is worse than
 * the generic sentence it replaced: it names the wrong act with authority.
 *
 * So the latest submission wins, whatever it did. A later act that SUCCEEDED
 * silences an earlier refusal, because the person got an answer to the thing
 * they last pressed; a later act still in flight silences it too, and speaks
 * for itself if it refuses. Acts never submitted (`submittedAt === 0`) are not
 * in the running at all. Two acts submitted in the same millisecond keep the
 * order they were listed in, which is as close to "last" as a tie can get.
 */
export function lastRefusedAct<K extends string>(
  acts: Readonly<Record<K, SubmittedAct>>,
): { readonly act: K; readonly error: unknown } | null {
  let latest: { act: K; state: SubmittedAct } | null = null
  for (const [act, state] of Object.entries(acts) as [K, SubmittedAct][]) {
    if (state.submittedAt > (latest?.state.submittedAt ?? 0)) {
      latest = { act, state }
    }
  }
  return latest !== null && latest.state.isError
    ? { act: latest.act, error: latest.state.error }
    : null
}
